/**
 * appChromeTransition — "a piece of app chrome is animating its width right now" (VRG-512)
 *
 * ── THE PROBLEM: EVERY CONSUMER LEARNS ABOUT THE SIDEBAR ONE FRAME TOO LATE ──────────
 *
 * The sidebar collapses with a CSS `width` transition (`Sidebar.tsx`'s `openedMixin` /
 * `closedMixin`). Everything downstream of it — the analytics canvas, the charts inside
 * it, the scan map — finds out by OBSERVING the resulting box:
 *
 *   sidebar width changes (CSS, per frame)
 *     -> layout
 *       -> ResizeObserver fires (post-layout, by spec)
 *         -> requestAnimationFrame                     (react-grid-layout's useContainerWidth)
 *           -> setState -> React render -> new inline transform/width
 *             -> NEXT paint
 *
 * That chain cannot run in the same frame as the width change it is reacting to, so an
 * observer-driven consumer is structurally at least one frame behind the sidebar. One
 * frame is not what anyone notices. What they notice is what the lateness is then used
 * for: a consumer that reacts late and ALSO eases its own reaction is running a second
 * animation, started a frame late, against a target that moves again next frame.
 *
 * So the fix is not to make the observers faster — they cannot be — it is to stop the
 * downstream animations from existing. A card that repositions with NO transition of its
 * own tracks the container 1:1 at whatever speed the sidebar is moving, which is the
 * definition of "in sync": not two durations agreeing, but only one animation running.
 *
 * ── WHY A PUSHED SIGNAL AND NOT AN OBSERVED ONE ──────────────────────────────────────
 *
 * `AnalyticsCanvas` already derived this state by watching its own measured width change
 * (`containerResizing`), and that is one render TOO LATE by construction: the flag is set
 * in a passive effect, which runs after the commit that has already written the new item
 * positions with the 200ms ease still in force. So the very first frame of every toggle —
 * the largest single jump, and the one that sets the reader's impression — was eased
 * regardless. Every subsequent frame was correctly un-eased, which is exactly why the
 * symptom read as "it lags at the start and catches up".
 *
 * The sidebar, by contrast, KNOWS. Its toggle is a deliberate state change, not an
 * ambient resize, so it can say so before the first frame rather than after it. This
 * module is that announcement.
 *
 * ⚠️ IT IS NOT "THE SIDEBAR IS OPEN". It is "chrome is mid-transition", a strictly
 * temporary window. Consumers must use it only to SUPPRESS their own animation or
 * throttling, never to decide layout — a consumer that read it as a layout input would
 * render differently for ~200ms and then change, which is a worse defect than the one
 * this fixes.
 *
 * ── WHY `config/` AND WHY NO IMPORTS ─────────────────────────────────────────────────
 *
 * Consumers are `components/analytics/AnalyticsCanvas` and
 * `components/display/ScanMapPanel` — a page-level dashboard and a shared display
 * component. Neither may import from `components/navigation/Sidebar` (a display
 * component reaching up into app chrome is the layering inversion §47 is about), and the
 * sidebar's `open` state is local `useState` inside `Sidebar.tsx` anyway, so there is
 * nothing importable there even if it were allowed.
 *
 * This module therefore imports NOTHING but React, exactly as `analyticsCardGranularity`
 * does and for the same stated reason: a leaf with no imports cannot close a cycle, so
 * anything may read it. The naming is deliberately about CHROME rather than the sidebar,
 * because the Ask panel's slide (`useAskStore`'s `ASK_PANEL_SLIDE_DURATION_MS`) is the
 * same event with a different origin and should announce itself here too rather than
 * grow a second flag.
 *
 * ── THE SHAPE IS `markStore`'s, DELIBERATELY ─────────────────────────────────────────
 *
 * `Map`-less because there is only one chrome, but otherwise the same module store behind
 * `useSyncExternalStore` that `markStore` (`ScansByTime2.tsx`) and
 * `analyticsCardGranularity` already use, for the identical reason: the writer and the
 * readers are siblings with no common owner below the app root, and lifting the state
 * would mean threading a prop through every layout component between them. Reusing the
 * established mechanism rather than inventing a third one is §28/§36.
 */
import { useSyncExternalStore } from 'react'

/**
 * Extra time held past the announced duration, in ms.
 *
 * A CSS transition's `transitionend` and the last ResizeObserver delivery do not land on
 * the same frame, and observer delivery is not guaranteed on every frame under load. A
 * window that closed exactly on the nominal duration would therefore release the
 * suppression while one final measurement was still in flight, which re-arms the very
 * 200ms ease this exists to prevent — on the last frame instead of the first. One frame
 * at 60fps is ~16ms; 60 is a few frames of headroom and is still far shorter than any
 * deliberate gesture that might follow.
 */
const TRANSITION_TAIL_MS = 60

let transitioning = false
let endTimer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

const emit = () => listeners.forEach(fn => fn())

const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * Announce that a piece of chrome has just STARTED a width transition of `durationMs`.
 *
 * ⚠️ CALL THIS FROM A LAYOUT EFFECT, NOT A PASSIVE ONE, and not from an event handler
 * that runs before the state change commits. The whole value of this module over the
 * observed flag it replaces is that the suppression is in force for the FIRST frame; a
 * passive effect reintroduces exactly the one-render lateness described above.
 *
 * `durationMs` is passed rather than hardcoded because the sidebar's open and close are
 * NOT the same length — MUI's `enteringScreen` is 225ms and `leavingScreen` is 195ms, and
 * this module holding its own copy of either would be a second source for a number it
 * does not own. The caller reads its real duration from the theme and reports it.
 *
 * Re-entrant: a second call while a window is open extends it rather than starting a
 * second one, so rapidly toggling the sidebar keeps one continuous suppression instead of
 * flickering between them.
 */
export const beginChromeTransition = (durationMs: number): void => {
  if (endTimer !== null) clearTimeout(endTimer)

  const wasTransitioning = transitioning
  transitioning = true

  endTimer = setTimeout(() => {
    endTimer = null
    transitioning = false
    emit()
  }, durationMs + TRANSITION_TAIL_MS)

  // Only notify on the rising edge. An extension of an open window changes no subscriber's
  // answer, and emitting anyway would re-render every consumer mid-animation for nothing.
  if (!wasTransitioning) emit()
}

/** The plain, non-React read — for observers and callbacks outside a component. */
export const isChromeTransitioning = (): boolean => transitioning

const getSnapshot = () => transitioning

/**
 * True while app chrome is mid-transition.
 *
 * Suppress your own transition/debounce while this is true. Do NOT branch layout on it.
 */
export const useChromeTransitioning = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
